import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import {
  defaultTranslations,
  getAvailableLanguages,
  pruneUserTranslations,
  mergeTranslations,
  getTranslation,
  TRANSLATION_KEYS,
} from "@cadenza/i18n-core";

const I18nContext = createContext();
const LOCAL_KEY = "riva_i18n";

const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const I18nProvider = ({ children }) => {
  // load user settings
  const persisted = loadPersisted();
  const [language, setLanguage] = useState(persisted.language || "en");
  const [userTranslations, setUserTranslations] = useState(
    persisted.userTranslations || {}
  );

  // prune invalid user entries
  const safeUser = useMemo(
    () => pruneUserTranslations(userTranslations, defaultTranslations),
    [userTranslations]
  );
  // merge defaults + user overrides
  const merged = useMemo(
    () => mergeTranslations(defaultTranslations, safeUser),
    [safeUser]
  );

  // compute available languages and labels
  const availableLanguages = useMemo(
    () => getAvailableLanguages(merged),
    [merged]
  );
  const labelsByCode = useMemo(() => merged.language || {}, [merged]);

  // build runtime STRINGS map
  const STRINGS = useMemo(() => {
    const stringsObj = Object.fromEntries(
      Object.values(TRANSLATION_KEYS).map((key) => [
        key,
        getTranslation(key, language, merged),
      ])
    );
    return stringsObj;
  }, [language, merged]);

  // persist any changes
  useEffect(() => {
    localStorage.setItem(
      LOCAL_KEY,
      JSON.stringify({ language, userTranslations })
    );
  }, [language, userTranslations]);

  const contextValue = useMemo(
    () => ({
      language,
      setLanguage,
      userTranslations,
      setUserTranslations,
      availableLanguages,
      labelsByCode,
      STRINGS,
    }),
    [language, userTranslations, availableLanguages, labelsByCode, STRINGS]
  );

  if (!merged || !merged.language) {
    return <div>Loading translations...</div>;
  }

  return (
    <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>
  );
};

// Hook for consuming context
export const useLanguageContext = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useLanguageContext must be used within I18nProvider");
  }
  return ctx;
};
